/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { IAddScheduledTask, INodeFileMgmt } from "../../../../store/api/tasks/types";
import { useStoreDispatch } from "../../../../store/hooks";
import { nodeFileMgmtDefault, prepareDataForNodeFileMgmt } from "../../helpers";
import { useSelector } from "react-redux";
import { isEditTaskSelector, tasksSelector } from "../../../../store/api/tasks/selector";
import { errorMsgSelector } from "../../../../store/helpers/helperSelectors";
import { setScheduledTask } from "../../../../store/api/tasks/reducer";
import * as uuid from "uuid";
import { setErrorMsg, setOpenCustomDailog } from "../../../../store/helpers/helperReducers";
import { GridRowId } from "@mui/x-data-grid-pro";
import { fileMgmtModuleList } from "../../../../globals/constants";
import { isError } from "../../../../utils/validations";
import { IKeyValue } from "../../../Administrations/types";

export const useNodeFileMgmtData = () => {
  const dispatch = useStoreDispatch();
  const errorMsg = useSelector(errorMsgSelector);
  const scheduledtasks: IAddScheduledTask = useSelector(tasksSelector);
  const { nodeFileMgmt = [], nodeFileMgmtAllInString, password } = scheduledtasks;
  const [nodeFileMgmtFormData, setNodeFileMgmtFormData] = useState<INodeFileMgmt>(nodeFileMgmtDefault);
  const [selectedRowIds, setSelectedRowIds] = useState<GridRowId[]>([]);
  const [modelOpenMode, setModelOpenMode] = useState("");

  const isEditTask = useSelector(isEditTaskSelector);

  useEffect(() => {
    if (isEditTask) {
      dispatch(
        setScheduledTask({
          nodeFileMgmt: prepareDataForNodeFileMgmt(nodeFileMgmtAllInString!, password!),
          password: password,
        })
      );
    } else {
      dispatch(
        setScheduledTask({
          nodeFileMgmt: [],
          password: "",
          nodeFileMgmtAllInString: "",
        })
      );
    }
  }, []);
  const addNewNodeFileMgmt = () => {
    setModelOpenMode("Add");
    setNodeFileMgmtFormData(nodeFileMgmtDefault);
    dispatch(setOpenCustomDailog(true));
    dispatch(setErrorMsg({}));
  };
  const deleteNodeFileMgmtRecord = () => {
    if (nodeFileMgmt && nodeFileMgmt?.length > 0 && selectedRowIds.length === 1) {
      let nodeFileMgmtString: string = "";
      let passString: string = "";
      const afterRemovedSelected = nodeFileMgmt.filter((item: INodeFileMgmt) => item.id !== selectedRowIds[0]);
      if (afterRemovedSelected.length > 0) {
        let newNodeFileMgmtString: string = "";
        afterRemovedSelected.forEach((item: INodeFileMgmt) => {
          newNodeFileMgmtString = `${item?.moduleId},${item?.maxAttemptDuration},${item?.maxAttempts},${item?.randomUploadMaxDelay},${item?.hostName},${item?.username},${item?.generateSCOSSSHKey},${item?.autoPopulatedAuthorizedKeyFile},${item?.forwordingGroupIndex},${item?.overwriteMode},${item?.priority},${item?.protocol},${item?.remotePath},${item?.remotePermission}`;
          if (nodeFileMgmtString.length <= 0) {
            nodeFileMgmtString = newNodeFileMgmtString;
            passString = item?.password;
          } else {
            nodeFileMgmtString = `${nodeFileMgmtString}|${newNodeFileMgmtString}`;
            passString = `${passString}|${item?.password}`;
          }
        });
      }
      dispatch(setScheduledTask({ nodeFileMgmt: afterRemovedSelected, nodeFileMgmtAllInString: nodeFileMgmtString, password: passString }));
    }
  };
  const addToFileMgmtListHandler = () => {
    let nodeFileMgmtList: INodeFileMgmt[] = [];
    let nodeFileMgmtString: string = "";
    let passString: string = "";
    let inputError: IKeyValue = {};
    //********************************validation on save (Add to list) ********************************************

    Object.entries(nodeFileMgmtFormData).forEach(([key, value]) => {
      if (key === "moduleId" && value === fileMgmtModuleList[3].value) {
        inputError = { ...inputError, [key]: `Module ID must be specified!` };
        return;
      } else if (key === "password") {
        inputError = { ...inputError, [key]: !nodeFileMgmtFormData.generateSCOSSSHKey && !!!value ? `This field is required!` : "" };
      } else {
        if (!value && (value === "" || (value && value.trim() === ""))) {
          inputError = { ...inputError, [key]: `This field is required!` };
        }
      }
    });
    //********************************end validation section ****************************************
    dispatch(setErrorMsg(inputError));
    if (!isError(inputError)) {
      if (modelOpenMode === "Edit") {
        if (nodeFileMgmt.length > 0) {
          nodeFileMgmtList = nodeFileMgmt.filter((item: INodeFileMgmt) => item.id !== nodeFileMgmtFormData.id);
          nodeFileMgmtList.push(nodeFileMgmtFormData);
        }
      } else {
        const uid = uuid.v4(); // generate the unique id for each row
        nodeFileMgmtList = [{ ...nodeFileMgmtFormData, id: uid }, ...nodeFileMgmt];
      }
      if (nodeFileMgmtList.length > 0) {
        let newNodeFileMgmtString: string = "";
        nodeFileMgmtList.forEach((item: INodeFileMgmt) => {
          newNodeFileMgmtString = `${item?.moduleId},${item?.maxAttemptDuration},${item?.maxAttempts},${item?.randomUploadMaxDelay},${item?.hostName},${item?.username},${item?.generateSCOSSSHKey},${item?.autoPopulatedAuthorizedKeyFile},${item?.forwordingGroupIndex},${item?.overwriteMode},${item?.priority},${item?.protocol},${item?.remotePath},${item?.remotePermission}`;
          if (nodeFileMgmtString.length <= 0) {
            nodeFileMgmtString = newNodeFileMgmtString;
            passString = item?.password;
          } else {
            nodeFileMgmtString = `${nodeFileMgmtString}|${newNodeFileMgmtString}`;
            passString = `${passString}|${item?.password}`;
          }
        });
      }
      dispatch(setScheduledTask({ nodeFileMgmt: nodeFileMgmtList, nodeFileMgmtAllInString: nodeFileMgmtString, password: passString }));
      dispatch(setOpenCustomDailog(false));
      setModelOpenMode("");
    }
  };

  return {
    dispatch,
    errorMsg,
    setErrorMsg,
    scheduledtasks,
    nodeFileMgmtFormData,
    setNodeFileMgmtFormData,
    addToFileMgmtListHandler,
    addNewNodeFileMgmt,
    deleteNodeFileMgmtRecord,
    selectedRowIds,
    setSelectedRowIds,
    modelOpenMode,
    setModelOpenMode,
    nodeFileMgmtAllInString,
  };
};
